"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GraphQLRequest = exports.connectionRequestFactory = exports.APQ_NOT_FOUND_CODE = exports.defaultRequestOpts = void 0;
const tslib_1 = require("tslib");
const fp_1 = require("@ihr-radioedit/fp");
const sdk_network_1 = require("@ihr-radioedit/sdk-network");
const response_1 = require("./response");
const utils_1 = require("./utils");
/**
 * GraphQLRequest is instantiated with these default options if no other options
 * are passed in. Since this is an object reference it can be tweaked to update
 * how things are invoked during client configuration.
 */
exports.defaultRequestOpts = {
    useAPQ: true,
    tolerateErrors: true,
};
exports.APQ_NOT_FOUND_CODE = "PersistedQueryNotFound";
/**
 * This is a simple callable designed to accept a query and return
 */
exports.connectionRequestFactory = (conn) => (opName, type, query) => {
    return new GraphQLRequest(conn, type, query, opName);
};
// Variables and Extensions parameters get JSON serialized and then escaped. This function can do that.
const encodeObject = (obj) => encodeURIComponent(JSON.stringify(obj !== null && obj !== void 0 ? obj : ""));
class GraphQLRequest {
    constructor(conn, type, gql, opName, options) {
        this.conn = conn;
        this.type = type;
        this.gql = gql;
        this.opName = opName;
        this.options = Object.assign(Object.assign({}, exports.defaultRequestOpts), (options !== null && options !== void 0 ? options : {}));
    }
    get client() {
        return this.conn.http;
    }
    query(variables, options) {
        const combinedOpts = Object.assign(Object.assign({}, this.options), (options !== null && options !== void 0 ? options : {}));
        const extensions = { persistedQuery: { version: 1, sha256Hash: utils_1.shaStr(this.gql) } };
        const doGet = () => {
            const params = {
                operationName: this.opName,
                variables: encodeObject(variables !== null && variables !== void 0 ? variables : ""),
                extensions: encodeObject(extensions),
            };
            return fp_1.pipe(this.request("GET", { params }), 
            // If the get responded with an APQ error, let's try a post. Otherwise just keep response.
            // Apollo is a shit head, and only reads post body OR query string, never both.
            // https://github.com/apollographql/apollo-server/issues/3547
            fp_1.TE.chain(response => response.hasErrorCondition(exports.APQ_NOT_FOUND_CODE) ? doPost(true) : fp_1.TE.right(response)));
        };
        const doPost = (withAPQ = false) => {
            const data = {
                operationName: this.opName,
                variables: variables !== null && variables !== void 0 ? variables : {},
                query: this.gql,
                extensions: withAPQ ? extensions : undefined,
            };
            return this.request("POST", { data });
        };
        const convertError = (task) => fp_1.pipe(task, 
        // If there are errors and tolerateErrors isn't enabled, make this a left.
        fp_1.TE.filterOrElse(response => !response.hasError || combinedOpts.tolerateErrors, response => {
            return new response_1.GraphQLError("GraphQL Error", response);
        }));
        // Add APQ extension config if enabled.
        if (this.type === "Query" && combinedOpts.useAPQ) {
            // Optimistiacally try this with a GET
            return fp_1.pipe(doGet(), convertError);
        }
        else {
            return fp_1.pipe(doPost(false), convertError);
        }
    }
    /**
     * Workaround for non FP-TS code bases. This is pretty common, though using
     * fp-ts is still recommended.
     */
    queryAsPromise(variables, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const response = yield this.query(variables, options)();
            if (fp_1.E.isLeft(response)) {
                throw response.left;
            }
            return response.right;
        });
    }
    request(method, config) {
        return fp_1.pipe(fp_1.TE.tryCatch(() => tslib_1.__awaiter(this, void 0, void 0, function* () { return this.client.request(Object.assign({ method }, config)); }), (reason) => reason), fp_1.TE.filterOrElse(response => String(response.headers["content-type"]).includes("application/json"), response => new sdk_network_1.HTTPError(`Expected response type to contain 'application/json', got ${response.headers["content-type"]}`, response)), fp_1.TE.map(response => response_1.GraphQLResponse.fromResponse(response.data)));
    }
}
exports.GraphQLRequest = GraphQLRequest;
//# sourceMappingURL=request.js.map